"use client";

import { useEffect, useState } from "react";
import { fetchWithAuth } from "@/lib/fetchWithAuth";

/**
 * 현재 로그인 사용자의 아바타.
 * - avatarFileId 가 있으면 /api/users/me/avatar 로 Bearer 인증 후 blob 렌더
 * - 없거나 로드 실패 시 username 이니셜로 fallback
 */
export function UserAvatar({
  avatarFileId,
  username,
  className = "",
}: {
  avatarFileId: string | null | undefined;
  username?: string | null;
  className?: string;
}) {
  const [image, setImage] = useState<{ fileId: string; src: string | null; failed: boolean } | null>(null);

  useEffect(() => {
    if (!avatarFileId) return;

    let cancelled = false;
    let createdUrl: string | null = null;
    const apiUrl = process.env.NEXT_PUBLIC_API_URL || 'http://localhost:3000';
    fetchWithAuth(`${apiUrl}/api/files/${avatarFileId}/content`, { skipRedirectOn401: true })
      .then(res => (res.ok ? res.blob() : null))
      .then(blob => {
        if (!blob || cancelled) {
          if (!cancelled) setImage({ fileId: avatarFileId, src: null, failed: true });
          return;
        }
        createdUrl = URL.createObjectURL(blob);
        setImage({
          fileId: avatarFileId,
          src: createdUrl,
          failed: false,
        });
      })
      .catch(() => {
        if (!cancelled) setImage({ fileId: avatarFileId, src: null, failed: true });
      });

    return () => {
      cancelled = true;
      if (createdUrl) URL.revokeObjectURL(createdUrl);
    };
  }, [avatarFileId]);

  const currentImage = image?.fileId === avatarFileId ? image : null;
  const src = currentImage?.src ?? null;
  const failed = currentImage?.failed ?? false;

  const base = `rounded-full overflow-hidden flex items-center justify-center shrink-0 border border-border ${className}`;

  if (avatarFileId && src && !failed) {
    return (
      <div className={base}>
        <img src={src} alt={username || 'avatar'} className="w-full h-full object-cover" />
      </div>
    );
  }

  return (
    <div className={`${base} bg-gradient-to-tr from-gray-100 to-gray-200 dark:from-gray-700 dark:to-gray-600 text-gray-700 dark:text-gray-200 font-black`}>
      {(username || '').charAt(0).toUpperCase() || 'U'}
    </div>
  );
}
